// A vertical status timeline after Isomorphic's logistics tracking timeline, as the mocks use it
// for a run's life (design/isomorphic/src/pages/run-side.tsx): done steps filled, the current
// one pulsing orange, later ones hollow.
import { Check } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { cn } from "./cn";

export type StepState = "done" | "current" | "todo";

export interface TimelineStep {
  key: string;
  label: ReactNode;
  state: StepState;
  at?: ReactNode;
  detail?: ReactNode;
  /** Under the detail: folded error or system text (ui/OutputPeek). */
  extra?: ReactNode;
}

export function Timeline({ steps, className }: { steps: TimelineStep[]; className?: string }) {
  return (
    <ol className={cn("relative m-0 list-none p-0", className)}>
      {steps.map((s, i) => (
        <li key={s.key} className="relative flex gap-3.5 pb-5 last:pb-0" aria-current={s.state === "current" ? "step" : undefined}>
          {i < steps.length - 1 ? (
            <span className={cn("absolute start-[13px] top-7 h-[calc(100%-24px)] w-0.5", s.state === "done" ? "bg-primary" : "bg-gray-200")} />
          ) : null}
          <span
            className={cn(
              "relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
              s.state === "done" && "bg-primary text-primary-foreground",
              s.state === "current" && "bg-orange text-white ring-4 ring-orange/20 dark:text-gray-0",
              s.state === "todo" && "border-2 border-gray-200 bg-gray-0 dark:bg-gray-50",
            )}
          >
            {s.state === "done" ? <Check weight="bold" className="h-3.5 w-3.5" /> : null}
            {s.state === "current" ? <span className="h-2 w-2 animate-pulse rounded-full bg-white" /> : null}
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className={cn("font-semibold", s.state === "todo" ? "text-gray-500" : "text-gray-900")}>{s.label}</span>
              {s.at ? <span className="whitespace-nowrap font-mono text-[11.5px] text-gray-500">{s.at}</span> : null}
            </div>
            {s.detail ? <div className="text-xs text-gray-500 [overflow-wrap:anywhere]">{s.detail}</div> : null}
            {s.extra ? <div className="mt-1 min-w-0 space-y-1">{s.extra}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}
